import { getFirst, getLast, getRandomItem } from "../../utils/array-utils"
import { Cents, Chord, Instrument, Melody, Performance, Pitch, pitch, PitchedNote, TimedChord } from "../music"
import { Exercise, Grade, Result, Subject } from "../subject"

/**
 * In this subject, the tuning of the instruments in an ensemble must
 * be adjusted.
 */
export const ensembleTuningSubject =
		new class extends Subject<EnsembleTuningExercise, EnsembleTuningResult> {

	id = "ensemble-tuning"
	name = "Ensemble einstimmen"
	description = "Korrigiere die Intonation der Instrumente eines Ensembles für einen sauberen Zusammenklang"
	page = "EnsembleTuningPage"

	/** Possible tunings. */
	readonly tunings = [-50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50] as Cents[]

	createCustomExercise(possibleTunings: Cents[]): EnsembleTuningExercise {
		let instruments: EnsembleInstrument[]
		if (Math.random() < 0.5) {
			//create a woodwind quartet (flute, oboe, clarinet, bassoon)
			instruments = [flute, oboe, clarinet, bassoon].map(
					it => it.withMistuning(getRandomItem(possibleTunings)))
		}
		else {
			//create a brass quintet (2 trumpets, horn, trombone, tuba)
			instruments = [trumpet, trumpet, horn, trombone, tuba].map(
					it => it.withMistuning(getRandomItem(possibleTunings)))
			for (let i of [1, 2]) //trumpet 1 and 2
				instruments[i-1] = instruments[i-1].withName(instruments[i-1].name + " " + i)
		}
		return new EnsembleTuningExercise(instruments)
	}

	createExercise(): EnsembleTuningExercise {
		return this.createCustomExercise(this.tunings)
	}

	/** Grades the overall tuning of the given results. */
	grade(results: EnsembleTuningResult[]): Grade {
		const resultCounts = results.length
		if (resultCounts == 0)
			return 0
		//each result counts (1/resultCounts) of the total result
		//full points for correct answer, 0 points for maximum wrong answer, linearly interpolated
		const weight = 1 / resultCounts
		const maxMistuning = getLast(this.tunings)
		let grade = 0 as Grade
		for (let result of results)
			grade += (1 - (Math.abs(result.tuning) / maxMistuning)) * weight
		return grade
	}
}()

/**
 * An exercise for tuning the given ensemble.
 */
export class EnsembleTuningExercise implements Exercise {

	//the tuning fork instrument, which is always perfectly tuned
	tuningFork: EnsembleInstrument
	//the current mistuning in cents for each instrument
	currentMistunings: Cents[]

	constructor(public instruments: EnsembleInstrument[]) {
		this.currentMistunings = instruments.map(it => it.mistuning)
		this.tuningFork = new EnsembleInstrument("TuningFork", "Stimmgabel", pitch("Bb4"), 0)
	}

	/**
	 * Call this method when the user is finished with tuning the ensemble.
	 */
	finish(): Grade {
		return ensembleTuningSubject.grade(this.currentMistunings.map(
				it => ({tuning: it})))
	}

	/**
	 * Gets the tuning sound for the given instrument or for all instruments,
	 * if the given instrument is null. The default pitch is played, unless a custom pitch is given.
	 */
	getPerformance(instrument: EnsembleInstrument = null, pitch: Pitch = null): Performance {
		let melodies: Melody[] = []
		let instruments = (instrument != null ? [instrument] : this.instruments)
		for (const instrument of instruments) {
			const mistuning = this.getMistuning(instrument)
			const chord = new Chord([new PitchedNote(pitch || instrument.pitch, mistuning)], 3)
			const timedChord = new TimedChord(chord, 0)
			melodies.push(new Melody([timedChord], instrument.kind, 1))
		}
		return new Performance(melodies)
	}

	/**
	 * Gets the current mistuning of the given instrument. Works also for the tuning fork.
	 */
	getMistuning(instrument: EnsembleInstrument) {
		const index = this.instruments.indexOf(instrument)
		return (index > -1 ? this.currentMistunings[index] : instrument.mistuning)
	}

	/**
	 * If possible, tunes up the given instrument one step.
	 */
	tuneUp(instrument: EnsembleInstrument) {
		this.tune(instrument, 10)
	}

	/**
	 * If possible, tunes down the given instrument one step.
	 */
	tuneDown(instrument: EnsembleInstrument) {
		this.tune(instrument, -10)
	}

	/**
	 * If possible, tunes up the given instrument by the given amount of cents.
	 * the new mistuning is never below or above the allowed range.
	 */
	tune(instrument: EnsembleInstrument, delta: Cents) {
		const index = this.instruments.indexOf(instrument)
		if (index > -1) {
			let newMistuning = this.currentMistunings[index] + delta
			const range = ensembleTuningSubject.tunings
			newMistuning = Math.max(newMistuning, getFirst(range))
			newMistuning = Math.min(newMistuning, getLast(range))
			this.currentMistunings[index] = newMistuning
		}
	}

}

/**
 * Each instrument in the ensemble.
 */
export class EnsembleInstrument {

	constructor(
		/** The kind of instrument */
		public readonly kind: Instrument,
		/** The displayed name, like "Trumpet 1" */
		public readonly name: string,
		/** The tuning pitch */
		public readonly pitch: Pitch,
		/** The original tuning error in cents, when the exercise begins */
		public readonly mistuning: Cents
	) {}

	withName(name: string) {
		return new EnsembleInstrument(this.kind, name, this.pitch, this.mistuning)
	}

	withMistuning(mistuning: Cents) {
		return new EnsembleInstrument(this.kind, this.name, this.pitch, mistuning)
	}

	getImgSrc(): string {
		let img: string = this.kind
		if (["EbClarinet", "BassClarinet", "Bassoon"].indexOf(this.kind) > -1) {
			//image still missing
			img = "Other"
		}
		return `assets/img/instruments/${img}.png`
	}

}

const flute = new EnsembleInstrument("Flute", "Flute", pitch("Bb5"), 0)
const oboe = new EnsembleInstrument("Oboe", "Oboe", pitch("Bb4"), 0)
const ebClarinet = new EnsembleInstrument("EbClarinet", "Clarinet in Eb", pitch("Bb5"), 0)
const clarinet = new EnsembleInstrument("Clarinet", "Clarinet", pitch("Bb4"), 0)
const altoSax = new EnsembleInstrument("AltoSax", "Alto Sax", pitch("Bb4"), 0)
const bassClarinet = new EnsembleInstrument("BassClarinet", "Bass Clarinet", pitch("Bb3"), 0)
const bassoon = new EnsembleInstrument("Bassoon", "Bassoon", pitch("Bb3"), 0)
const trumpet = new EnsembleInstrument("Trumpet", "Trumpet", pitch("Bb4"), 0)
const horn = new EnsembleInstrument("Horn", "Horn", pitch("Bb3"), 0)
const trombone = new EnsembleInstrument("Trombone", "Trombone", pitch("Bb3"), 0)
const tuba = new EnsembleInstrument("Tuba", "Tuba", pitch("Bb2"), 0)

/**
 * Result for ensemble tuning.
 */
export interface EnsembleTuningResult extends Result {
	tuning: Cents
}


// WEBPACK FOOTER //
// ./src/model/exercises/ensemble-tuning.ts